/**
 * Development-only persistence for Explore Unknown POI positions (localStorage).
 * Coordinates are **authoring space** (same as `exploreUnknownPortalLayout.js`: 1920×1080 canvas).
 *
 * Not used in production builds — callers must gate on `process.env.NODE_ENV === 'development'`.
 */

const STORAGE_KEY_PREFIX = 'exploreUnknownDevWaypoints_';

/** Must match `exploreUnknownPortalLayout.js` waypoint counts per surface. */
const WAYPOINT_COUNT_BY_SURFACE = {
  arrival: 5,
  cave: 5,
  desert: 6,
  fair: 7,
  lavender: 5,
  treetop: 1,
  boat: 5,
  pixelpast: 6,
  kendrex: 6,
};

function getExpectedWaypointCount(surface) {
  const n = WAYPOINT_COUNT_BY_SURFACE[surface];
  if (typeof n !== 'number') {
    throw new Error(
      `exploreUnknownWaypointDevStorage: surface must be "arrival", "cave", "desert", "fair", "lavender", "treetop", "boat", "pixelpast", or "kendrex", got ${JSON.stringify(surface)}`
    );
  }
  return n;
}

function assertValidSurface(surface) {
  getExpectedWaypointCount(surface);
}

function assertValidPoints(points, surface) {
  const expectedLen = getExpectedWaypointCount(surface);
  if (!Array.isArray(points) || points.length !== expectedLen) {
    throw new Error(
      `exploreUnknownWaypointDevStorage: points must be an array of length ${expectedLen}, got ${points?.length}`
    );
  }
  for (let i = 0; i < points.length; i += 1) {
    const p = points[i];
    if (!Array.isArray(p) || p.length !== 2) {
      throw new Error(`exploreUnknownWaypointDevStorage: point ${i} must be [x, y]`);
    }
    const [x, y] = p;
    if (typeof x !== 'number' || typeof y !== 'number' || !Number.isFinite(x) || !Number.isFinite(y)) {
      throw new Error(`exploreUnknownWaypointDevStorage: point ${i} must be finite numbers`);
    }
  }
}

/**
 * @param {'arrival'|'cave'|'desert'|'fair'|'lavender'|'treetop'|'boat'|'pixelpast'|'kendrex'} surface
 * @returns {number[][] | null} parsed points, or null if nothing stored / invalid JSON (caller may treat null as "use layout file")
 */
function loadDevWaypoints(surface) {
  assertValidSurface(surface);
  let raw;
  try {
    raw = window.localStorage.getItem(`${STORAGE_KEY_PREFIX}${surface}`);
  } catch (err) {
    throw new Error(`exploreUnknownWaypointDevStorage: localStorage read failed (${err?.message ?? err})`);
  }
  if (raw == null || raw === '') {
    return null;
  }
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    throw new Error(`exploreUnknownWaypointDevStorage: stored JSON is invalid (${err?.message ?? err})`);
  }
  assertValidPoints(parsed, surface);
  return parsed;
}

/**
 * @param {'arrival'|'cave'|'desert'|'fair'|'lavender'|'treetop'|'boat'|'pixelpast'|'kendrex'} surface
 * @param {number[][]} points
 */
function saveDevWaypoints(surface, points) {
  assertValidSurface(surface);
  assertValidPoints(points, surface);
  try {
    window.localStorage.setItem(`${STORAGE_KEY_PREFIX}${surface}`, JSON.stringify(points));
  } catch (err) {
    throw new Error(`exploreUnknownWaypointDevStorage: localStorage write failed (${err?.message ?? err})`);
  }
}

/**
 * @param {'arrival'|'cave'|'desert'|'fair'|'lavender'|'treetop'|'boat'|'pixelpast'|'kendrex'} surface
 */
function clearDevWaypoints(surface) {
  assertValidSurface(surface);
  try {
    window.localStorage.removeItem(`${STORAGE_KEY_PREFIX}${surface}`);
  } catch (err) {
    throw new Error(`exploreUnknownWaypointDevStorage: localStorage remove failed (${err?.message ?? err})`);
  }
}

module.exports = {
  loadDevWaypoints,
  saveDevWaypoints,
  clearDevWaypoints,
  getExpectedWaypointCount,
  WAYPOINT_COUNT_BY_SURFACE,
};
